'use client';
import CardFormSchema, { CardFormSchemaProps } from '@/schema/CardForm';
import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import CreditCardIcon from '@mui/icons-material/CreditCard';
import { verifyEmail } from '@/services/email/resources';
import { ProductDetailProps } from '@/types/general';
import Image, { StaticImageData } from 'next/image';
import ButtonWithLoading from '../ButtonWithLoading';
import InfoPainels from '../InfoPainels';
import { useLocale, useTranslations } from 'next-intl';
import style from './style.module.css';
import Select from '../Select';
import {
  countries,
  getCallingCodeByCountryCode,
  getCountryCodeByCallingCode,
} from '@/data/countries';
import { FlagIcon } from 'react-flag-kit';
import { getPhoneFormatByCountryCode } from '@/data/phone';
import { GrowthControl } from '@/utils/growthControl';
import { AsYouTypeFormatter, PhoneNumberUtil } from 'google-libphonenumber';

export interface CardFormProps {
  productFormDetails?: ProductDetailProps[];
  imageCard: StaticImageData | string;
  title: string;
  titleFormCard?: string;
  buttonLabel?: string;
  buttonLabelRedirecting?: string;
  linkButton?: string;
  country?: string;
  vertical?: 'cc' | 'emp';
}

export default function ProductForm({
  productFormDetails = [],
  imageCard,
  title,
  titleFormCard,
  buttonLabel,
  buttonLabelRedirecting,
  linkButton,
  country,
  vertical,
}: CardFormProps) {
  const locale = useLocale();
  const countryCode = locale.split('-')[1];

  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);

  const savedName = nameInputRef.current ? nameInputRef.current.value : '';
  const savedEmail = emailInputRef.current ? emailInputRef.current.value : '';

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitted },
    setValue,
    getValues,
  } = useForm<CardFormSchemaProps>({
    resolver: zodResolver(CardFormSchema),
    defaultValues: {
      name: savedName,
      email: savedEmail,
      phone: '',
      callingCode: getCallingCodeByCountryCode(countryCode),
    },
  });

  const callingCodeInitialValue = getValues('callingCode');

  const [isImageError, setImageError] = useState(false);
  const [phonePlaceholder, setPhonePlaceholder] = useState<string>(
    getPhoneFormatByCountryCode(countryCode)
  );
  const [countryToMask, setCountryToMask] = useState<string>(countryCode);
  const phoneNumberFormatter = new AsYouTypeFormatter(countryToMask);

  useEffect(() => {
    const newCountryTomMask = getCountryCodeByCallingCode(
      callingCodeInitialValue
    );
    const newPhoneFormat = getPhoneFormatByCountryCode(newCountryTomMask);
    setPhonePlaceholder(newPhoneFormat);
    setCountryToMask(newCountryTomMask);
  }, [callingCodeInitialValue]);

  const handleImageLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.target;
    if (naturalWidth === 0 || (naturalWidth === 1 && naturalHeight === 1)) {
      setImageError(true);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const t = useTranslations('Index');

  useEffect(() => {
    (async () => {
      await GrowthControl.track('Product View', {
        country: country,
        vertical: vertical,
      });
    })();
  }, []);

  const onSubmit: SubmitHandler<CardFormSchemaProps> = async (data) => {
    try {
      const { callingCode, name, email, phone } = data;

      const { isValid: isEmailValid, email: validatedEmail } =
        await verifyEmail(email);

      if (isEmailValid) {
        // OneControl expects UUID.UUID; empty userId lets the SDK use projectId.anonymousId
        await GrowthControl.identify('', {
          email: validatedEmail,
          name: name,
          phone: phone ? `${callingCode}${phone.replace(/\D/g, '')}` : '',
        });
      }

      await GrowthControl.track('Product Capture Completed', {
        country,
        vertical,
      });
    } catch (error) {
      console.error('Error handling form submission:', error);
    }
  };

  const generateCountryOptions = () => {
    return countries.map((country) => ({
      label: (
        <div className={style.countryCallingCode}>
          <FlagIcon className={style.countryFlag} code={country.code} />
          {country.callingCode}
        </div>
      ),
      value: country.callingCode,
    }));
  };

  const redirect = {
    url: linkButton,
    isAllowed: isSubmitted,
  };

  const termsOfUseLink =
    locale === 'pt-BR'
      ? 'https://utua.com.br/termos-de-uso/'
      : 'https://utua.com.br/terms-of-use/';

  const privacyPolicyLink =
    locale === 'pt-BR'
      ? 'https://utua.com.br/politica-de-privacidade/'
      : 'https://utua.com.br/privacy-policy/';

  return (
    <div className={style.containerCardForm}>
      <div className={style.contentImage}>
        {!isImageError ? (
          <Image
            className={style.imageCard}
            alt="Card Image"
            src={imageCard}
            width={166}
            height={166}
            onLoad={handleImageLoad}
            onError={handleImageError}></Image>
        ) : (
          <CreditCardIcon className={style.genericCreditCardIcon} />
        )}
        <h3 className={style.cardTitle}>{title}</h3>

        <div className={style.contentInfoPainelsForm}>
          <InfoPainels
            infoTitle={t('detailsInfoForm.detailsTitle')}
            data={productFormDetails}
            layout={productFormDetails.length === 1 ? 'column' : 'grid'}
          />
        </div>
      </div>

      <form className={style.containerForm} onSubmit={handleSubmit(onSubmit)}>
        <p className={style.titleForm}>{titleFormCard}</p>
        <div className={style.contentForm}>
          <Controller
            control={control}
            name="name"
            render={({ field: { onChange, value } }) => {
              return (
                <div className={style.formInputs}>
                  <label className={style.textLabel} htmlFor="name">
                    {t('form.name')}
                  </label>
                  <input
                    className={`${style.textInputs} ${
                      errors.name ? style.errorInput : ''
                    }`}
                    ref={nameInputRef}
                    type="text"
                    name="name"
                    id="name"
                    onChange={onChange}
                    placeholder={t('form.placeholderName')}
                    value={value}
                  />
                  {errors.name && (
                    <p className={`${style.error} ${style.errorMessage}`}>
                      {errors.name.message}
                    </p>
                  )}
                </div>
              );
            }}
          />

          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value } }) => {
              return (
                <div className={style.formInputs}>
                  <label className={style.textLabel} htmlFor="email">
                    {t('form.email')}
                  </label>
                  <input
                    className={`${style.textInputs} ${
                      errors.email ? style.errorInput : ''
                    }`}
                    ref={emailInputRef}
                    type="email"
                    name="email"
                    id="email"
                    onChange={onChange}
                    placeholder={t('form.placeholderEmail')}
                    value={value}
                  />
                  {errors.email && (
                    <p className={`${style.error} ${style.errorMessage}`}>
                      {errors.email.message}
                    </p>
                  )}
                </div>
              );
            }}
          />

          <div className={style.formInputs}>
            <label className={style.textLabel} htmlFor="number">
              {t('form.phone')}
            </label>
            <div className={style.formPhone}>
              <Controller
                control={control}
                name="callingCode"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Select
                      options={generateCountryOptions()}
                      name="callingCode"
                      value={value}
                      onChange={(e) => {
                        setValue('phone', '');
                        onChange(e);
                      }}
                    />
                  );
                }}
              />

              <Controller
                control={control}
                name="phone"
                render={({ field: { onChange, value } }) => {
                  const handlePhoneChange = (
                    e: React.ChangeEvent<HTMLInputElement>
                  ) => {
                    const { value: targetValue } = e.target;
                    const parsedValue = targetValue.replace(/[^0-9]/g, '');

                    let formattedPhone = parsedValue;
                    for (let i = 0; i < parsedValue.length; i++) {
                      formattedPhone = phoneNumberFormatter.inputDigit(
                        parsedValue[i]
                      );
                    }

                    phoneNumberFormatter.clear();
                    onChange(formattedPhone);
                  };

                  return (
                    <div className={style.phoneInput}>
                      <input
                        className={`${style.textInputs} ${
                          errors.phone ? style.errorInput : ''
                        }`}
                        type="tel"
                        name="phone"
                        id="phone"
                        onChange={handlePhoneChange}
                        value={value}
                        placeholder={phonePlaceholder}
                      />
                    </div>
                  );
                }}
              />
            </div>

            {errors.phone && (
              <p className={`${style.error} ${style.errorMessage}`}>
                {errors.phone.message}
              </p>
            )}
          </div>
          <p className={style.textTerms}>
            {t('form.descriptionTerms')}{' '}
            <a
              className={style.termsOfUse}
              target="_blank"
              href={termsOfUseLink}>
              {t('form.termsOfUse')}
            </a>{' '}
            e{' '}
            <a
              className={style.termsOfUse}
              target="_blank"
              href={privacyPolicyLink}>
              {t('form.privacyPolicy')}
            </a>
          </p>
        </div>
        <ButtonWithLoading
          labelButtonSend={buttonLabel}
          labelButtonRedirect={buttonLabelRedirecting}
          formDataIsValid={isValid}
          redirect={redirect}
        />
      </form>
    </div>
  );
}
